import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logoGold from "@/assets/Logosinfondogoldpng.png?format=webp&width=128";

const INSTAGRAM_URL = "https://www.instagram.com/atelier_ritual/";

const RitualFooter = () => {
  const { t } = useTranslation();

  const atelierLinks = [
    { label: t("footer.silhouettes"), to: "/#silhouettes" },
    { label: t("footer.freeConsultation"), to: "/consultation" },
    { label: t("footer.myStory"), to: "/about/our-story" },
    { label: t("footer.theExperience"), to: "/#sustainability" },
    { label: t("footer.letters"), to: "/journal" },
    { label: t("footer.sustainability"), to: "/about/sustainability" },
  ];

  const helpLinks = [
    { label: t("footer.sizeGuide"), to: "/about/size-guide" },
    { label: t("footer.customerCare"), to: "/about/customer-care" },
    { label: t("footer.storeLocator"), to: "/about/store-locator" },
    { label: t("footer.privacyPolicy"), to: "/privacy-policy" },
    { label: t("footer.termsOfService"), to: "/terms-of-service" },
  ];

  const connectLinks = [
    {
      label: t("footer.instagram"),
      href: INSTAGRAM_URL,
      external: true,
    },
    {
      label: t("footer.pinterest"),
      href: "https://www.pinterest.ca/",
      external: true,
    },
    {
      label: "Email us",
      href: "mailto:ritualwear.co@gmail.com",
      external: false,
    },
  ];

  const badges = [
    t("footer.badge1"),
    t("footer.badge2"),
    t("footer.badge3"),
    t("footer.badge4"),
    t("footer.badge5"),
  ];

  const NavLink = ({
    item,
  }: {
    item: { label: string; to: string };
  }) => (
    <li>
      <Link
        to={item.to}
        className="group flex items-center gap-2.5 font-body text-xs text-primary-foreground/65 hover:text-primary-foreground transition-colors duration-300"
      >
        <span className="block h-px bg-primary-foreground/20 w-3 group-hover:w-5 group-hover:bg-gold transition-all duration-300 shrink-0" />
        {item.label}
      </Link>
    </li>
  );

  return (
    <footer className="w-full bg-foreground text-primary-foreground">

      {/* — Decorative top band — */}
      <div className="w-full border-t border-primary-foreground/6">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between gap-6 overflow-hidden">
          {/* Left marquee line */}
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-primary-foreground/10 to-transparent" />

          {/* Logo mark */}
          <div className="shrink-0 flex flex-col items-center gap-1.5">
            <img
              src={logoGold}
              alt="Ritual logo"
              className="w-16 h-16 object-contain opacity-60"
            />
          </div>

          {/* Right marquee line */}
          <div className="flex-1 h-px bg-gradient-to-l from-transparent via-primary-foreground/10 to-transparent" />
        </div>
      </div>

      {/* — Main grid — */}
      <div className="max-w-6xl mx-auto px-6 pt-10 pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-14 mb-16">

          {/* Brand column */}
          <div className="lg:col-span-1">
            <h3 className="font-display text-xl tracking-widest text-primary-foreground mb-1">
              Ritual
            </h3>
            <p className="font-body text-[9px] tracking-[0.4em] uppercase text-gold mb-7">
              by Camila Luna
            </p>
            <p className="font-body text-sm text-primary-foreground/75 leading-relaxed mb-8">
              {t("footer.tagline")}
            </p>

            {/* Contact */}
            <div className="space-y-2 mb-8">
              <a
                href="mailto:ritualwear.co@gmail.com"
                className="block font-body text-xs text-primary-foreground/65 hover:text-gold transition-colors duration-300"
              >
                ritualwear.co@gmail.com
              </a>
              <p className="font-body text-xs text-primary-foreground/55">
                Chelsea, Québec, CA
              </p>
            </div>

            {/* Instagram CTA */}
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2.5 border border-primary-foreground/15 hover:border-gold/50 px-4 py-2.5 transition-all duration-400"
              aria-label="Follow on Instagram"
            >
              {/* Instagram SVG */}
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                className="w-3.5 h-3.5 text-gold/60 group-hover:text-gold transition-colors duration-300 shrink-0"
                aria-hidden="true"
              >
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <circle cx="12" cy="12" r="4" />
                <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none" />
              </svg>
              <span className="font-body text-[9px] tracking-[0.25em] uppercase text-primary-foreground/60 group-hover:text-gold/80 transition-colors duration-300">
                @atelier_ritual
              </span>
            </a>
          </div>

          </div>
        </div>

        {/* — Decorative values strip — */}
        <div className="border-t border-primary-foreground/10 py-8">
          <div className="flex flex-wrap justify-center items-center gap-x-5 gap-y-3">
            {badges.map((badge, i, arr) => (
              <span key={badge} className="flex items-center gap-5">
                <span className="font-body text-[9px] tracking-[0.28em] uppercase text-primary-foreground/70">
                  {badge}
                </span>
                {i < arr.length - 1 && (
                  <span className="w-[3px] h-[3px] rounded-full bg-gold/35" />
                )}
              </span>
            ))}
          </div>
        </div>

        {/* — Bottom bar — */}
        <div className="border-t border-primary-foreground/10 pt-5">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3">
            <p className="font-body text-[10px] tracking-wide text-primary-foreground/55">
              {t("footer.copyright")}
            </p>

            <div className="flex items-center gap-6">
              <Link
                to="/privacy-policy"
                className="font-body text-[10px] tracking-wide text-primary-foreground/55 hover:text-primary-foreground/90 transition-colors duration-300"
              >
                {t("footer.privacyPolicy")}
              </Link>
              <span className="w-px h-3 bg-primary-foreground/15" />
              <Link
                to="/terms-of-service"
                className="font-body text-[10px] tracking-wide text-primary-foreground/55 hover:text-primary-foreground/90 transition-colors duration-300"
              >
                {t("footer.termsOfService")}
              </Link>
              <span className="w-px h-3 bg-primary-foreground/15" />
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-body text-[10px] tracking-wide text-primary-foreground/55 hover:text-gold/70 transition-colors duration-300"
                aria-label="Instagram"
              >
                {/* Instagram SVG inline */}
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  className="w-3.5 h-3.5"
                  aria-hidden="true"
                >
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                  <circle cx="12" cy="12" r="4" />
                  <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none" />
                </svg>
              </a>
            </div>
          </div>
      </div>
    </footer>
  );
};

export default RitualFooter;
